import type { RequestOptions } from "@pkg/sdk/lib/sdks";
import type {
  Card3dsVerifyRequest,
  CardSetupIntent,
  CheckoutSessionPublic,
  CreateCardSetupIntentRes,
  PublicCardCheckoutRequest,
  PublicCardCheckoutResponse,
  PublicSavedCardsResponse,
  UpdateCardSetupIntentReq,
} from "@pkg/sdk/models";
import type { Connection } from "penpal";
import { connectToWindow } from ".";

// Note: this file is paired with customer-portal/src/routes/embeds/v1/tunnel-x
// The methods below should also be present in CheckoutTunnelX

type TunnelXIFrameMethods = {
  startPayment: () => void;
  confirmPayment: () => void;
  submitCheckout: (values: Record<string, string>) => Promise<unknown>;
  getCheckoutPreview: () => void;
  getCardSetupIntent: (cardSetupIntentId: string) => Promise<CardSetupIntent>;
  createCardSetupIntent: (secureToken: string) => Promise<CreateCardSetupIntentRes>;
  queryPublicEndpoint: (methodName: string, params: unknown, options?: RequestOptions) => Promise<unknown>;
};

type TunnelXParentMethods = Record<string, never>;

export type TunnelXIFrameConnection = Connection<TunnelXIFrameMethods>;

export type TunnelXParentConnection = Connection<TunnelXParentMethods>;

export const connectToTunnelXIframe = (iframe: HTMLIFrameElement, methods: TunnelXParentMethods) => {
  return connectToWindow<TunnelXIFrameMethods>({ window: iframe.contentWindow as Window, methods });
};

export const connectToTunnelXParent = (methods: TunnelXIFrameMethods) => {
  return connectToWindow<TunnelXParentMethods>({ window: window.parent, methods });
};

/**
 * SDK type for publicEndpoints proxy.
 * Matches the methods on PublicCors from @pkg/sdk that are called through queryPublicEndpoint.
 * Parameter types match the SDK's operation request types.
 * All methods accept an optional RequestOptions parameter for passing headers (e.g., x-request-id).
 */
type PublicEndpointsProxy = {
  createCardSetupIntent: (
    params: { checkoutToken: string },
    options?: RequestOptions,
  ) => Promise<CreateCardSetupIntentRes>;
  updateCardSetupIntent: (
    params: {
      checkoutToken: string;
      cardSetupIntentId: string;
      updateCardSetupIntentReq: UpdateCardSetupIntentReq;
    },
    options?: RequestOptions,
  ) => Promise<CardSetupIntent>;
  getCardSetupIntent: (
    params: { checkoutToken: string; cardSetupIntentId: string },
    options?: RequestOptions,
  ) => Promise<CardSetupIntent>;
  cardCheckout: (
    params: {
      checkoutToken: string;
      publicCardCheckoutRequest: PublicCardCheckoutRequest;
    },
    options?: RequestOptions,
  ) => Promise<PublicCardCheckoutResponse>;
  cardCheckoutVerify: (
    // card3dsVerifyRequest carries the native-3DS2 client result ({ details, paymentData })
    // for Adyen; omitted for redirect / VGS flows (body-less verify).
    params: { checkoutToken: string; card3dsVerifyRequest?: Card3dsVerifyRequest | null },
    options?: RequestOptions,
  ) => Promise<PublicCardCheckoutResponse>;
  // Session-scoped, PCI-safe list of the returning customer's vaulted cards for the
  // one-click saved-card checkout (CIT_SAVED_CARD_ONE_CLICK §14). Reached through the
  // same no-allowlist tunnel proxy; the underlying `sdk.publicCors.listSavedCards`
  // already exists and is bundled in the tunnel iframe. TS type only — the runtime
  // `Proxy` below forwards by method name.
  listSavedCards: (params: { checkoutToken: string }, options?: RequestOptions) => Promise<PublicSavedCardsResponse>;
  // Public session read. Carries `savedCardCvcReprompt`, the server-resolved flag the
  // one-click gate reads through `getSavedCardCvcReprompt()`.
  getCheckoutSessionByToken: (
    params: { checkoutToken: string },
    options?: RequestOptions,
  ) => Promise<CheckoutSessionPublic>;
};

export class TunnelXManager {
  private penpalConn: TunnelXIFrameConnection;
  public _methods: TunnelXIFrameMethods;

  static createFromPenpalConnection = async (penpalConn: TunnelXIFrameConnection) => {
    const methods = await penpalConn.promise;
    const manager = new TunnelXManager(penpalConn, methods);
    return manager;
  };

  private constructor(penpalConn: TunnelXIFrameConnection, penpalMethods: TunnelXIFrameMethods) {
    this.penpalConn = penpalConn;
    this._methods = penpalMethods;
  }

  /**
   * Makes publicEndpoints act like a real PK client, but actually forwards all calls
   * through queryPublicEndpoint and sends it through penpal.
   */
  get publicEndpoints(): PublicEndpointsProxy {
    const handler = {
      get: (_target: Record<string, never>, prop: string) => {
        return async (...args: unknown[]) => {
          return await this._methods.queryPublicEndpoint(prop, args[0], args[1] as RequestOptions | undefined);
        };
      },
    };
    return new Proxy({}, handler) as unknown as PublicEndpointsProxy;
  }

  destroy() {
    this.penpalConn.destroy();
  }
}
